import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  ArrowLeft, 
  ShoppingCart, 
  Filter, 
  X, 
  Search, 
  RefreshCw, 
  Gem
} from 'lucide-react'
import toast from 'react-hot-toast'
import useMarketStore from '../store/useMarketStore'
import useAppStore from '../store/useAppStore'
import useAudioManager from '../hooks/useAudioManager'

const Market = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { balance } = useAppStore()
  const { playSound } = useAudioManager({
    buy: 'buyItem'
  })
  
  const {
    skins,
    availableFilters,
    isLoading,
    filters,
    displayCount,
    totalLoaded,
    loadInitialData,
    applyFilters,
    updateFilter,
    resetFilters,
    loadMore,
    buySkin,
  } = useMarketStore()

  // Локальное состояние для UI
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [purchasingSkinsMap, setPurchasingSkinsMap] = useState({}) // { skinName: true/false }
  const [isMaxPriceAutofillEnabled, setIsMaxPriceAutofillEnabled] = useState(true)

  // Загрузка данных при монтировании
  useEffect(() => {
    loadInitialData()
  }, [])

  // Прокрутка наверх при монтировании
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  /**
   * Обработчик применения фильтров
   */
  const handleApplyFilters = async () => {
    try {
      if (isMaxPriceAutofillEnabled && filters.price_finish === 'any') {
        updateFilter('price_finish', String(balance))
      }

      await applyFilters()
      
      // Закрываем модалку
      setIsFilterModalOpen(false)
      
      // Скроллим наверх к результатам
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      toast.success(t('market.filtersApplied'))
    } catch (error) {
      toast.error(t('market.filtersApplyError'))
    }
  }

  /**
   * Обработчик сброса фильтров
   */
  const handleResetFilters = async () => {
    try {
      resetFilters()
      setIsMaxPriceAutofillEnabled(false)
      
      // Загружаем начальные данные (рандомные скины)
      await loadInitialData()
      
      // Закрываем модалку если открыта
      setIsFilterModalOpen(false)
      
      // Скроллим наверх
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      toast.success(t('market.filtersReset'))
    } catch (error) {
      toast.error(t('market.filtersResetError'))
    }
  }

  /**
   * Обработчик покупки скина
   */
  const handleBuySkin = async (skin) => {
    // Проверка баланса
    if (balance < skin.price) {
      toast.error(t('market.notEnoughCrystals'))
      return
    }

    // Устанавливаем состояние загрузки для конкретного скина
    setPurchasingSkinsMap(prev => ({ ...prev, [skin.name]: true }))

    try {
      await buySkin(skin.name)
      
      // Воспроизводим звук покупки
      playSound('buy')
      
      // Вибрация успешной покупки
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success')
      }
      
      // Отправляем цель в Яндекс.Метрику
      if (window.ym) {
        window.ym(104258304, 'reachGoal', 'buy-item')
      }
      
      toast.success(t('market.purchaseSuccess', { name: skin.name }))
    } catch (error) {
      // Обработка ошибок
      if (error.message?.includes('not_enough_balance')) {
        toast.error(t('market.notEnoughBalance'))
      } else if (error.message?.includes('error backend')) {
        toast.error(t('market.systemError'))
      } else {
        toast.error(t('market.purchaseError'))
      }
    } finally {
      // Убираем состояние загрузки
      setPurchasingSkinsMap(prev => ({ ...prev, [skin.name]: false }))
    }
  }

  // Получаем отображаемые скины (с учетом пагинации)
  const displayedSkins = skins.slice(0, displayCount)
  const hasMore = displayCount < totalLoaded

  const activeFiltersCount = Object.values(filters).filter((value) => value !== 'any' && value !== '').length
  const maxPriceFieldValue = filters.price_finish === 'any'
    ? (isMaxPriceAutofillEnabled ? String(balance) : '')
    : filters.price_finish

  const filterFields = (
    <>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" size={16} />
        <input
          type="text"
          placeholder={t('market.search')}
          value={filters.search_string === 'any' ? '' : filters.search_string}
          onChange={(e) => updateFilter('search_string', e.target.value || 'any')}
          disabled={isLoading}
          className="w-full pl-9 pr-9 py-2.5 bg-surface border border-white/20 rounded-lg text-base text-white placeholder-text-secondary focus:border-accent-primary focus:outline-none disabled:opacity-50"
        />
        {filters.search_string !== 'any' && (
          <button
            onClick={() => updateFilter('search_string', 'any')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div>
        <label className="block text-xs text-text-secondary mb-1.5">{t('market.exterior')}</label>
        <select
          value={filters.exterior}
          onChange={(e) => updateFilter('exterior', e.target.value)}
          disabled={isLoading}
          className="w-full px-2.5 py-2.5 bg-surface border border-white/20 rounded-lg text-base text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
        >
          <option value="any">{t('market.anyOption')}</option>
          {availableFilters.exterior?.map((ext) => (
            <option key={ext} value={ext}>{ext}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs text-text-secondary mb-1.5">{t('market.rarity')}</label>
        <select
          value={filters.rarity}
          onChange={(e) => updateFilter('rarity', e.target.value)}
          disabled={isLoading}
          className="w-full px-2.5 py-2.5 bg-surface border border-white/20 rounded-lg text-base text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
        >
          <option value="any">{t('market.anyOption')}</option>
          {availableFilters.rarity?.map((rar) => (
            <option key={rar} value={rar}>{rar}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs text-text-secondary mb-1.5">{t('market.type')}</label>
        <select
          value={filters.type}
          onChange={(e) => updateFilter('type', e.target.value)}
          disabled={isLoading}
          className="w-full px-2.5 py-2.5 bg-surface border border-white/20 rounded-lg text-base text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
        >
          <option value="any">{t('market.anyOption')}</option>
          {availableFilters.type?.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-text-secondary mb-1.5">{t('market.priceFrom')}</label>
          <input
            type="number"
            placeholder={t('market.priceMin')}
            value={filters.price_start === 'any' ? '' : filters.price_start}
            onChange={(e) => updateFilter('price_start', e.target.value || 'any')}
            disabled={isLoading}
            className="w-full px-2.5 py-2.5 bg-surface border border-white/20 rounded-lg text-base text-white placeholder-text-secondary focus:border-accent-primary focus:outline-none disabled:opacity-50"
          />
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1.5">{t('market.priceTo')}</label>
          <input
            type="number"
            placeholder={t('market.priceMax')}
            value={maxPriceFieldValue}
            onChange={(e) => {
              const inputValue = e.target.value
              if (!inputValue) {
                setIsMaxPriceAutofillEnabled(false)
                updateFilter('price_finish', 'any')
                return
              }

              updateFilter('price_finish', inputValue)
            }}
            disabled={isLoading}
            className="w-full px-2.5 py-2.5 bg-surface border border-white/20 rounded-lg text-base text-white placeholder-text-secondary focus:border-accent-primary focus:outline-none disabled:opacity-50"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-text-secondary mb-1.5">StatTrak</label>
        <select
          value={filters.stattrak}
          onChange={(e) => updateFilter('stattrak', e.target.value)}
          disabled={isLoading}
          className="w-full px-2.5 py-2.5 bg-surface border border-white/20 rounded-lg text-base text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
        >
          <option value="any">{t('market.anyOption')}</option>
          <option value="true">{t('market.stattrakOnly')}</option>
          <option value="false">{t('market.stattrakNone')}</option>
        </select>
      </div>

      <div>
        <label className="block text-xs text-text-secondary mb-1.5">Souvenir</label>
        <select
          value={filters.souvenir}
          onChange={(e) => updateFilter('souvenir', e.target.value)}
          disabled={isLoading}
          className="w-full px-2.5 py-2.5 bg-surface border border-white/20 rounded-lg text-base text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
        >
          <option value="any">{t('market.anyOption')}</option>
          <option value="true">{t('market.souvenirOnly')}</option>
          <option value="false">{t('market.souvenirNone')}</option>
        </select>
      </div>
    </>
  )

  const renderFilterActions = (wrapperClassName) => (
    <div className={wrapperClassName}>
      <button
        onClick={handleApplyFilters}
        disabled={isLoading}
        className="flex-1 py-2.5 bg-accent-primary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-all disabled:opacity-50 text-sm"
      >
        {t('market.apply')}
      </button>
      <button
        onClick={handleResetFilters}
        disabled={isLoading}
        className="px-4 py-2.5 bg-surface border border-white/20 text-white font-semibold rounded-lg hover:bg-white/5 transition-all disabled:opacity-50 text-sm"
      >
        {t('market.reset')}
      </button>
    </div>
  )

  return (
    <div className="min-h-screen bg-dark-primary text-white pb-6 lg:pb-10">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-dark-primary/95 backdrop-blur-sm border-b border-white/10 lg:border-none">
        <div className="relative mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8 lg:py-4 lg:mt-4">
          <div className="flex items-center gap-4 lg:flex-1">
            <div className="flex items-center gap-2 lg:absolute lg:left-1/2 lg:-translate-x-1/2 lg:pointer-events-none">
              <ShoppingCart size={24} className="w-6 h-6 shrink-0 lg:w-8 lg:h-8 text-accent-primary" />
              <h1 className="text-2xl font-bold text-accent-primary lg:text-4xl">
                {t('market.title')}
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/payment')}
            className="flex items-center gap-2 bg-surface px-4 py-2 rounded-lg border border-accent-primary/30 hover:border-accent-primary transition-all cursor-pointer hover:scale-105 lg:hidden"
          >
            <Gem size={18} className="text-accent-primary" />
            <span className="font-bold text-accent-primary">{balance.toLocaleString()}</span>
          </button>
        </div>
      </div>

      {/* Модальное окно фильтров */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black z-50 flex justify-center lg:hidden">
          <div className="bg-black w-full sm:max-w-md h-full sm:h-auto sm:max-h-[85vh] sm:rounded-xl overflow-hidden flex flex-col">
            {/* Заголовок модалки */}
            <div className="bg-black border-b border-white/10 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Filter size={18} />
                {t('market.filters')}
              </h2>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Содержимое модалки - скроллируемое */}
            <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">
              {filterFields}
            </div>

            {/* Кнопки - прилипшие снизу */}
            {renderFilterActions('border-t border-white/10 p-3 flex gap-2 flex-shrink-0 bg-black')}
          </div>
        </div>
      )}

      {/* Основной контент - сетка скинов */}
      <div className="px-4 pt-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-6">
          <div className="hidden lg:block">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-2xl border border-white/10 bg-surface p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Filter size={18} className="text-accent-primary" />
                    <h2 className="text-lg font-bold text-white">{t('market.filters')}</h2>
                  </div>
                  {activeFiltersCount > 0 && (
                    <span className="inline-flex min-w-7 items-center justify-center rounded-full bg-accent-primary/15 px-2 py-1 text-xs font-bold text-accent-primary">
                      {activeFiltersCount}
                    </span>
                  )}
                </div>
                <div className="space-y-3">
                  {filterFields}
                </div>
                {renderFilterActions('mt-4 flex gap-2')}
              </div>
            </div>
          </div>

          <div className="lg:min-w-0">
            {/* Состояние загрузки */}
            {isLoading && skins.length === 0 && (
              <div className="flex justify-center items-center py-12 lg:min-h-[320px]">
                <RefreshCw className="animate-spin text-accent-primary" size={32} />
              </div>
            )}

            {/* Пустое состояние - нет результатов */}
            {!isLoading && skins.length === 0 && (
              <div className="text-center py-12 lg:py-24 max-w-md mx-auto">
                <div className="mb-6 flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-accent-primary/10 flex items-center justify-center">
                    <ShoppingCart className="w-10 h-10 text-accent-primary" />
                  </div>
                </div>
                <p className="text-text-secondary mb-4 text-lg">
                  {t('market.noResults')}
                </p>
                <p className="text-text-secondary mb-6">
                  {t('market.noResultsDescription')}
                </p>
                <button
                  onClick={handleResetFilters}
                  className="px-6 py-3 bg-accent-primary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-colors"
                >
                  {t('market.resetFilters')}
                </button>
              </div>
            )}

            {/* Сетка скинов */}
            {!isLoading && displayedSkins.length > 0 && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-6 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {displayedSkins.map((skin, index) => {
                    const isPurchasing = purchasingSkinsMap[skin.name]
                    const canAfford = balance >= skin.price

                    return (
                      <div
                        key={`${skin.name}-${index}`}
                        className="bg-surface rounded-xl overflow-hidden border-2 transition-all duration-200 hover:border-accent-primary/70 lg:hover:-translate-y-1 lg:hover:shadow-xl lg:hover:shadow-accent-primary/10 flex flex-col"
                        style={{ borderColor: `#${skin.color}60` }}
                      >
                        {/* Изображение скина */}
                        <div className="aspect-square bg-dark-primary/50 relative flex items-center justify-center p-2 lg:p-3">
                          <img
                            src={skin.img}
                            alt={skin.name}
                            className="max-w-full max-h-full object-contain"
                            loading="lazy"
                          />
                          
                          {/* Badges для StatTrak/Souvenir */}
                          <div className="absolute top-2 left-2 flex flex-col gap-1">
                            {skin.name.includes('StatTrak™') && (
                              <span className="px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded shadow-lg">
                                ST
                              </span>
                            )}
                            {skin.name.includes('Souvenir') && (
                              <span className="px-2 py-1 bg-yellow-400 text-black text-xs font-bold rounded shadow-lg">
                                SV
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Информация о скине */}
                        <div className="p-3 lg:p-4 flex flex-col flex-1">
                          {/* Название скина */}
                          <h3 className="text-sm lg:text-base font-bold text-white mb-2 line-clamp-2">
                            {skin.name.includes('|') ? (
                              skin.name.split('|')[0].trim()
                            ) : (
                              skin.name
                            )}
                          </h3>
                          
                          {/* Состояние (если есть) */}
                          {skin.name.includes('|') && (
                            <p className="text-xs lg:text-sm text-text-secondary mb-2 line-clamp-2">
                              {skin.name.split('|')[1].trim()}
                            </p>
                          )}

                          {/* Цена и кнопка - прижаты к низу */}
                          <div className="mt-auto">
                            {/* Цена */}
                            <div className="py-1 mb-2">
                              <div className="flex items-center justify-center gap-1.5">
                              <Gem size={18} className="text-accent-primary" />
                              <span className="text-accent-primary font-bold text-lg lg:text-xl">
                                {skin.price.toLocaleString()}
                              </span>
                              </div>
                              {skin.price_currency && (
                                <p className="text-xs lg:text-sm text-text-secondary text-center mt-0.5">
                                  {skin.price_currency}
                                </p>
                              )}
                            </div>

                            {/* Кнопка покупки */}
                            {canAfford ? (
                              <button
                                onClick={() => handleBuySkin(skin)}
                                disabled={isPurchasing}
                                className="w-full py-3 bg-accent-primary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {isPurchasing ? (
                                  <RefreshCw size={20} className="animate-spin" />
                                ) : (
                                  <ShoppingCart size={20} />
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={() => navigate('/payment')}
                                className="w-full py-3 bg-blue-500 text-white font-bold rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2"
                              >
                                <Gem size={20} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Кнопка "Показать еще" */}
                {hasMore && (
                  <div className="flex justify-center mb-12">
                    <button
                      onClick={loadMore}
                      className="px-6 py-3 bg-surface border border-accent-primary/30 text-white font-semibold rounded-lg hover:bg-surface/80 hover:border-accent-primary transition-all"
                    >
                      {t('market.loadMore', { count: 30 })}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Прилипшая кнопка "Фильтры" внизу экрана (над навигацией) */}
      <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-30 lg:hidden">
        <button
          onClick={() => setIsFilterModalOpen(true)}
          className="px-8 py-4 bg-accent-primary text-dark-primary font-bold rounded-full shadow-lg shadow-accent-primary/30 hover:shadow-xl hover:shadow-accent-primary/50 transition-all flex items-center gap-2"
        >
          <Filter size={20} />
          {t('market.filters')}
        </button>
      </div>
    </div>
  )
}

export default Market
